/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Defines the visual layout template for TilePreview.
 * The screen passes the appropriate template based on carousel type and platform.
 */
export enum TilePreviewTemplate {
  /**
   * Browser 4-line preview: title, subtitle, channel info, day & time range.
   *
   * Example:
   * ┌─────────────────────────────────┐
   * │ Breaking Bad                    │ ← Line 1: title
   * │ S1 E5 - Gray Matter             │ ← Line 2: subtitle (episode info)
   * │ AMC (150)                       │ ← Line 3: channel info
   * │ Today · 6:00-7:30pm             │ ← Line 4: day & time range
   * └─────────────────────────────────┘
   */
  BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW = "BROWSER_LANDSCAPE_HORIZONTAL_PREVIEW",

  /**
   * Title + subtitle only, no meta. Recently watched channel preview (browser & SmartTV).
   *
   * Example:
   * ┌──────────────────────┐
   * │ Friends              │ ← Line 1: title
   * │ S1 :E1 - Pilot       │ ← Line 2: subtitle
   * └──────────────────────┘
   */
  LOGO_PIP_LANDSCAPE_PREVIEW = "LOGO_PIP_LANDSCAPE_PREVIEW",

  /**
   * SmartTV 3-line live content preview: title, subtitle, rating + channel + time.
   *
   * Example:
   * ┌───────────────────────────────────────────┐
   * │ Breaking Bad                              │ ← Line 1: title
   * │ S1 E5 - Gray Matter                       │ ← Line 2: subtitle
   * │ TV-14 HBO(200) · Today · 6:00-7:30pm      │ ← Line 3: rating + channel info + day & time range
   * └───────────────────────────────────────────┘
   */
  LIVE_LANDSCAPE_HORIZONTAL_PREVIEW = "LIVE_LANDSCAPE_HORIZONTAL_PREVIEW",

  /**
   * SmartTV 3-line non live content preview: title, subtitle, rating + time.
   *
   * Example:
   * ┌───────────────────────────────────────────┐
   * │ Breaking Bad                              │ ← Line 1: title
   * │ S1 E5 - Gray Matter                       │ ← Line 2: subtitle
   * │ TV-14 · Today · 6:00-7:30pm               │ ← Line 3: rating + day & time range
   * └───────────────────────────────────────────┘
   */
  NON_LIVE_LANDSCAPE_HORIZONTAL_PREVIEW = "NON_LIVE_LANDSCAPE_HORIZONTAL_PREVIEW",

  /**
   * Top 10 For You movie poster preview: title + "Movie · year · rating" subtitle.
   *
   * Example:
   * ┌─────────────────────┐
   * │ Inception           │ ← Line 1: title
   * │ Movie · 2010 · PG13 │ ← Line 2: subtitle with year and rating
   * └─────────────────────┘
   */
  MOVIE_POSTER_HORIZONTAL = "MOVIE_POSTER_HORIZONTAL",

  /**
   * Top 10 For You show poster preview: title + "Show · rating" subtitle.
   *
   * Example:
   * ┌─────────────────────┐
   * │ [Poster Image]      │
   * │                     │
   * │ Stranger Things     │ ← Line 1: title
   * │ Show · TV-MA        │ ← Line 2: subtitle with rating
   * └─────────────────────┘
   */
  SHOW_POSTER_HORIZONTAL = "SHOW_POSTER_HORIZONTAL",

  /**
   * Now Playing PIP preview. Title + subtitle only, no meta.
   *
   * Example:
   * ┌──────────────────────┐
   * │ Game of Thrones      │ ← Line 1: title
   * │ HBO (123)            │ ← Line 2: channelName (channel no)
   * └──────────────────────┘
   */
  LIVE_PIP_LANDSCAPE_PREVIEW = "LIVE_PIP_LANDSCAPE_PREVIEW",

  /**
   * Series recording preview: title + number of recordings.
   *
   * Example:
   * ┌──────────────────────┐
   * │ Yellowstone          │ ← Line 1: title
   * │ 3 recordings         │ ← Line 2: number of recordings
   * └──────────────────────┘
   */
  SERIES_RECORDING_HORIZONTAL = "SERIES_RECORDING_HORIZONTAL",

  /**
   * Movie recording preview: title + "duration · rating" subtitle.
   *
   * Example:
   * ┌──────────────────────────┐
   * │ Inception                │ ← Line 1: title
   * │ 2h 43mins · PG-13        │ ← Line 2: length of the recording · rating
   * └──────────────────────────┘
   */
  MOVIE_RECORDING_HORIZONTAL = "MOVIE_RECORDING_HORIZONTAL"
}
